/**
 * Audit Review Page — Bulk-select workflow
 * 
 * All suggestions start checked. Uncheck the ones you don't want,
 * then hit "Approve All Checked" to bulk-approve the rest.
 *
 * Keyboard: space=toggle check, ↑↓=navigate, ctrl+a=select all, ctrl+shift+a=deselect all
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Archive, Plus, Eye, Wrench, ChevronDown, ChevronRight,
    Check, X, SkipForward, RefreshCw, Loader2, ArrowRight,
    AlertTriangle, Filter, CheckSquare, Square, Trash2
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : '/api';

const TABLE_META = [
    { value: 'all', label: 'All Tables', icon: '📋' },
    { value: 'kinks', label: 'Kinks', icon: '🔥' },
    { value: 'sex_actions', label: 'Actions', icon: '⚡' },
    { value: 'activities', label: 'Activities', icon: '🎯' },
    { value: 'archetypes', label: 'Archetypes', icon: '🎭' },
    { value: 'body_parts', label: 'Body Parts', icon: '🫦' },
    { value: 'dynamics', label: 'Dynamics', icon: '⚖️' },
    { value: 'gear', label: 'Gear', icon: '🔧' },
    { value: 'characters', label: 'Characters', icon: '👤' },
    { value: 'scenarios', label: 'Scenarios', icon: '🎬' },
    { value: 'positions', label: 'Positions', icon: '🤸' },
    { value: 'arousal_mechanics', label: 'Mechanics', icon: '🧠' },
    { value: 'arousal_effects', label: 'Effects', icon: '✨' },
];

const PRIORITY_COLORS = {
    P0: 'bg-red-500/20 text-red-400 border-red-500/30',
    P1: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    P2: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    P3: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const ACTION_ICONS = { archive: Archive, review: Eye, add: Plus, fix: Wrench };
const ACTION_COLORS = { archive: 'text-red-400', review: 'text-yellow-400', add: 'text-green-400', fix: 'text-blue-400' };

// ─── Compact Row ────────────────────────────────────────────────────────────
function SuggestionRow({ suggestion, isChecked, isActive, onToggle, onClick }) {
    const ActionIcon = ACTION_ICONS[suggestion.action] || Eye;
    const actionColor = ACTION_COLORS[suggestion.action] || 'text-gray-400';
    const prioClass = PRIORITY_COLORS[suggestion.priority] || PRIORITY_COLORS.P3;

    return (
        <div
            onClick={onClick}
            className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm
                ${isActive
                    ? 'border-wine-500/50 bg-dark-800/80 ring-1 ring-wine-500/15'
                    : 'border-transparent hover:border-dark-700 hover:bg-dark-800/30'}
                ${!isChecked ? 'opacity-40' : ''}
            `}
        >
            {/* Checkbox */}
            <button
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                className="flex-shrink-0 text-dark-400 hover:text-white transition-colors"
            >
                {isChecked
                    ? <CheckSquare className="w-4 h-4 text-green-500" />
                    : <Square className="w-4 h-4" />}
            </button>

            {/* Action icon */}
            <ActionIcon className={`w-3.5 h-3.5 flex-shrink-0 ${actionColor}`} />

            {/* Entity name(s) */}
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                {suggestion.action === 'add' ? (
                    <>
                        <span className="text-green-400 font-medium truncate">+ {suggestion.addEntity}</span>
                        {suggestion.addCategory && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-400 flex-shrink-0">
                                {suggestion.addCategory}
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        <span className={`font-medium truncate ${suggestion.action === 'archive' ? 'text-red-300' : 'text-yellow-300'}`}>
                            {suggestion.archiveEntity}
                        </span>
                        {suggestion.keepEntity && (
                            <>
                                <ArrowRight className="w-3 h-3 text-dark-600 flex-shrink-0" />
                                <span className="text-green-300 truncate">{suggestion.keepEntity}</span>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Reason */}
            <span className="text-dark-500 text-xs truncate max-w-[200px] hidden lg:inline">
                {suggestion.reason}
            </span>

            {/* Priority */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono flex-shrink-0 ${prioClass}`}>
                {suggestion.priority}
            </span>
        </div>
    );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function AuditReviewPage() {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [tableFilter, setTableFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [actionFilter, setActionFilter] = useState('all');
    const [checked, setChecked] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [stats, setStats] = useState(null);
    const [collapsed, setCollapsed] = useState(new Set());
    const listRef = useRef(null);

    // ─── Fetch ──────────────────────────────────────────────────────
    const fetchSuggestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ status: 'pending' });
            if (tableFilter !== 'all') params.set('table', tableFilter);
            if (priorityFilter !== 'all') params.set('priority', priorityFilter);
            if (actionFilter !== 'all') params.set('action', actionFilter);

            const res = await fetch(`${API_BASE}/audit/suggestions?${params}`);
            const data = await res.json();
            const items = data.suggestions || [];
            setSuggestions(items);
            // Auto-check all on load
            setChecked(new Set(items.map(s => s.id)));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [tableFilter, priorityFilter, actionFilter]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/audit/stats`);
            setStats(await res.json());
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetchSuggestions(); fetchStats(); }, [fetchSuggestions, fetchStats]);
    useEffect(() => { setActiveIndex(0); }, [tableFilter, priorityFilter, actionFilter]);

    // ─── Toast ──────────────────────────────────────────────────────
    const showToast = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ─── Check toggling ─────────────────────────────────────────────
    const toggleCheck = useCallback((id) => {
        setChecked(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const checkAll = useCallback(() => {
        setChecked(new Set(suggestions.map(s => s.id)));
    }, [suggestions]);

    const uncheckAll = useCallback(() => {
        setChecked(new Set());
    }, []);

    const toggleTableCheck = useCallback((tableName) => {
        const ids = suggestions.filter(s => s.table === tableName).map(s => s.id);
        setChecked(prev => {
            const next = new Set(prev);
            const allChecked = ids.every(id => next.has(id));
            ids.forEach(id => allChecked ? next.delete(id) : next.add(id));
            return next;
        });
    }, [suggestions]);

    // ─── Bulk actions ───────────────────────────────────────────────
    const bulkAction = useCallback(async (decision) => {
        const ids = [...checked];
        if (ids.length === 0) { showToast('Nothing selected', 'error'); return; }

        setActionLoading(true);
        try {
            const res = await fetch(`${API_BASE}/audit/bulk-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids, decision }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed');

            const icon = decision === 'approve' ? '✓' : decision === 'reject' ? '✗' : '→';
            showToast(`${icon} ${data.processed} items ${decision}d (${data.failed} failed)`, 'success');

            // Refresh
            fetchSuggestions();
            fetchStats();
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        } finally {
            setActionLoading(false);
        }
    }, [checked, showToast, fetchSuggestions, fetchStats]);

    // ─── Keyboard shortcuts ─────────────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === ' ') {
                e.preventDefault();
                const s = suggestions[activeIndex];
                if (s) toggleCheck(s.id);
                return;
            }
            if (e.key === 'ArrowDown' || e.key === 'j') {
                e.preventDefault();
                setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
            }
            if (e.key === 'ArrowUp' || e.key === 'k') {
                e.preventDefault();
                setActiveIndex(i => Math.max(i - 1, 0));
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.shiftKey) {
                e.preventDefault();
                checkAll();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && e.shiftKey) {
                e.preventDefault();
                uncheckAll();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [suggestions, activeIndex, toggleCheck, checkAll, uncheckAll]);

    // Scroll active into view
    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeIndex]);

    // ─── Group by table ─────────────────────────────────────────────
    const grouped = useMemo(() => {
        const groups = new Map();
        suggestions.forEach((s, i) => {
            if (!groups.has(s.table)) groups.set(s.table, []);
            groups.get(s.table).push({ ...s, _idx: i });
        });
        return [...groups.entries()];
    }, [suggestions]);

    const checkedCount = checked.size;
    const totalVisible = suggestions.length;

    const pendingCount = stats?.byStatus?.pending || 0;
    const approvedCount = stats?.byStatus?.approved || 0;
    const rejectedCount = stats?.byStatus?.rejected || 0;
    const totalCount = stats?.total || 0;

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* ─── Header ─── */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="font-display font-bold text-2xl text-gray-100 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine-500/20 to-amber-500/20 flex items-center justify-center border border-wine-500/20">
                                <Filter className="w-5 h-5 text-wine-400" />
                            </div>
                            Entity Audit Review
                        </h1>
                        <p className="text-dark-500 text-sm mt-1 ml-[52px]">
                            Uncheck what you don't want, then bulk approve the rest
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Progress */}
                        <div className="text-right mr-2">
                            <div className="flex items-center gap-3 text-sm">
                                <span className="text-green-400 font-mono">{approvedCount}✓</span>
                                <span className="text-red-400 font-mono">{rejectedCount}✗</span>
                                <span className="text-dark-400 font-mono">{pendingCount} left</span>
                            </div>
                            {totalCount > 0 && (
                                <div className="w-48 h-1.5 bg-dark-800 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full flex">
                                        <div className="bg-green-500 transition-all" style={{ width: `${(approvedCount / totalCount) * 100}%` }} />
                                        <div className="bg-red-500 transition-all" style={{ width: `${(rejectedCount / totalCount) * 100}%` }} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <button onClick={() => { fetchSuggestions(); fetchStats(); }}
                            className="p-2 rounded-lg bg-dark-800 border border-dark-700 text-dark-400 hover:text-white transition-colors" title="Refresh">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* ─── Filters ─── */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    {/* Priority */}
                    <div className="flex bg-dark-900 border border-dark-700 rounded-lg p-0.5 gap-0.5">
                        {['all', 'P0', 'P1', 'P2', 'P3'].map(p => (
                            <button key={p} onClick={() => setPriorityFilter(p)}
                                className={`px-2.5 py-1.5 rounded-md text-xs font-mono font-medium transition-all ${priorityFilter === p
                                        ? p === 'all' ? 'bg-wine-600 text-white' :
                                            p === 'P0' ? 'bg-red-600 text-white' :
                                                p === 'P1' ? 'bg-orange-600 text-white' :
                                                    p === 'P2' ? 'bg-yellow-600 text-white' : 'bg-green-600 text-white'
                                        : 'text-dark-400 hover:text-dark-200'}`}>
                                {p === 'all' ? 'All' : p}
                            </button>
                        ))}
                    </div>
                    {/* Action */}
                    <div className="flex bg-dark-900 border border-dark-700 rounded-lg p-0.5 gap-0.5">
                        {['all', 'archive', 'review', 'add'].map(a => (
                            <button key={a} onClick={() => setActionFilter(a)}
                                className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all capitalize ${actionFilter === a ? 'bg-wine-600 text-white shadow-lg' : 'text-dark-400 hover:text-dark-200'}`}>
                                {a}
                            </button>
                        ))}
                    </div>
                    {/* Selection controls */}
                    <div className="flex items-center gap-2 ml-auto">
                        <span className="text-dark-500 text-xs">
                            {checkedCount}/{totalVisible} checked
                        </span>
                        <button onClick={checkAll}
                            className="px-2 py-1 rounded text-[11px] text-dark-400 hover:text-white bg-dark-800 border border-dark-700 transition-colors">
                            All
                        </button>
                        <button onClick={uncheckAll}
                            className="px-2 py-1 rounded text-[11px] text-dark-400 hover:text-white bg-dark-800 border border-dark-700 transition-colors">
                            None
                        </button>
                    </div>
                </div>

                {/* ─── Bulk Action Bar ─── */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-dark-900/80 rounded-xl border border-dark-700/50">
                    <button
                        onClick={() => bulkAction('approve')}
                        disabled={checkedCount === 0 || actionLoading}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <Check className="w-4 h-4" />
                        Approve {checkedCount} checked
                    </button>
                    <button
                        onClick={() => bulkAction('reject')}
                        disabled={checkedCount === 0 || actionLoading}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600/15 border border-red-500/20 text-red-400 hover:bg-red-600/25 font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <Trash2 className="w-4 h-4" />
                        Reject checked
                    </button>
                    {actionLoading && <Loader2 className="w-5 h-5 animate-spin text-wine-400 ml-2" />}
                    <span className="text-dark-600 text-xs ml-auto hidden md:inline">
                        space=toggle · ↑↓=navigate · ctrl+a=all · ctrl+shift+a=none
                    </span>
                </div>

                {/* ─── Main Layout ─── */}
                <div className="flex gap-4" style={{ height: 'calc(100vh - 310px)' }}>
                    {/* Table sidebar */}
                    <div className="w-44 flex-shrink-0 bg-dark-900/60 rounded-xl border border-dark-700/50 p-2 overflow-y-auto">
                        {TABLE_META.map((t, i) => {
                            const tableCount = t.value === 'all'
                                ? suggestions.length
                                : suggestions.filter(s => s.table === t.value).length;
                            const tableChecked = t.value === 'all'
                                ? checkedCount
                                : suggestions.filter(s => s.table === t.value && checked.has(s.id)).length;

                            if (t.value !== 'all' && tableCount === 0) return null;

                            return (
                                <button
                                    key={t.value}
                                    onClick={() => setTableFilter(t.value)}
                                    className={`w-full text-left px-2.5 py-2 rounded-lg text-sm flex items-center gap-2 transition-all mb-0.5 ${tableFilter === t.value
                                            ? 'bg-wine-600/20 text-white border border-wine-500/20'
                                            : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800/50 border border-transparent'
                                        }`}
                                >
                                    <span className="text-sm">{t.icon}</span>
                                    <span className="flex-1 truncate text-xs">{t.label}</span>
                                    {tableCount > 0 && (
                                        <span className="text-[10px] font-mono text-dark-500">
                                            {tableChecked}/{tableCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Card list */}
                    <div className="flex-1 overflow-y-auto pr-1" ref={listRef}>
                        {loading ? (
                            <div className="flex items-center justify-center h-full text-dark-500">
                                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...
                            </div>
                        ) : error ? (
                            <div className="flex items-center justify-center h-full text-red-400">
                                <AlertTriangle className="w-5 h-5 mr-2" /> {error}
                            </div>
                        ) : suggestions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-dark-500">
                                <div className="text-5xl mb-3">✓</div>
                                <h2 className="text-lg font-semibold text-dark-200 mb-1">Queue empty</h2>
                                <p className="text-sm">All suggestions reviewed!</p>
                            </div>
                        ) : (
                            <div className="space-y-0.5">
                                {grouped.map(([table, items]) => {
                                    const tableMeta = TABLE_META.find(t => t.value === table);
                                    const isCollapsed = collapsed.has(table);
                                    const tableItemIds = items.map(s => s.id);
                                    const allTableChecked = tableItemIds.every(id => checked.has(id));

                                    return (
                                        <div key={table}>
                                            {/* Group header */}
                                            <div className="sticky top-0 z-10 bg-charcoal/95 backdrop-blur-sm flex items-center gap-2 py-1.5 px-1">
                                                <button onClick={() => setCollapsed(prev => {
                                                    const n = new Set(prev);
                                                    n.has(table) ? n.delete(table) : n.add(table);
                                                    return n;
                                                })} className="text-dark-500 hover:text-dark-300">
                                                    {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                </button>
                                                <button
                                                    onClick={() => toggleTableCheck(table)}
                                                    className="text-dark-500 hover:text-white transition-colors"
                                                    title={allTableChecked ? 'Uncheck all in group' : 'Check all in group'}
                                                >
                                                    {allTableChecked
                                                        ? <CheckSquare className="w-3.5 h-3.5 text-green-500" />
                                                        : <Square className="w-3.5 h-3.5" />}
                                                </button>
                                                <span className="text-sm">{tableMeta?.icon || '📋'}</span>
                                                <span className="text-xs font-semibold uppercase tracking-wider text-dark-400">
                                                    {tableMeta?.label || table}
                                                </span>
                                                <span className="text-[10px] text-dark-600 font-mono">
                                                    ({items.filter(s => checked.has(s.id)).length}/{items.length})
                                                </span>
                                                <div className="flex-1 h-px bg-dark-800" />
                                            </div>
                                            {!isCollapsed && items.map(s => (
                                                <div key={s.id} data-idx={s._idx}>
                                                    <SuggestionRow
                                                        suggestion={s}
                                                        isChecked={checked.has(s.id)}
                                                        isActive={s._idx === activeIndex}
                                                        onToggle={() => toggleCheck(s.id)}
                                                        onClick={() => setActiveIndex(s._idx)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Toast */}
                {toast && (
                    <div className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-2xl text-sm font-medium z-50 ${toast.type === 'success' ? 'bg-green-600 text-white' :
                            toast.type === 'error' ? 'bg-red-600 text-white' :
                                'bg-dark-700 text-dark-100 border border-dark-600'
                        }`}>
                        {toast.msg}
                    </div>
                )}

                {/* Full-screen loading overlay */}
                {actionLoading && (
                    <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center">
                        <div className="bg-dark-900 rounded-xl border border-dark-700 px-8 py-6 flex flex-col items-center gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-wine-400" />
                            <span className="text-dark-300 text-sm">Processing {checkedCount} suggestions...</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
